'use client';

import { ClickAwayListener, Tooltip } from '@mui/material';
import { FC, ReactNode, useState, useEffect } from 'react';
import { IconBox } from './styled';
import { COLORS } from '@/constants';
import { HintIcon } from '@/components';

type HintProps = {
  message: string;
  children?: ReactNode;
  placement?: 'bottom-start' | 'right-start' | 'top';
};

const tooltipSlotProps = {
  tooltip: {
    sx: {
      bgcolor: '#fff',
      color: COLORS['neutral/800'],
      fontSize: '12px',
      lineHeight: 1.4,
      whiteSpace: 'pre-line',
      padding: '10px 12px',
      borderRadius: '6px',
      boxShadow: '0px 2px 10px 0px #0000001A',
    },
  },
};

export const Hint: FC<HintProps> = ({ message, children, placement = 'right-start' }) => {
  const [open, setOpen] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    setIsTouchDevice(window.matchMedia('(pointer: coarse)').matches);
  }, []);

  const handleClose = () => setOpen(false);
  const handleToggle = () => setOpen((prev) => !prev);

  if (children) {
    return (
      <Tooltip
        TransitionProps={{ timeout: 0 }}
        title={message}
        placement={placement}
        slotProps={tooltipSlotProps}
      >
        <span style={{ display: 'inline-flex' }}>{children}</span>
      </Tooltip>
    );
  }

  if (isTouchDevice) {
    return (
      <ClickAwayListener onClickAway={handleClose}>
        <Tooltip
          open={open}
          onClose={handleClose}
          onOpen={() => setOpen(true)}
          title={message}
          placement={placement === 'top' ? 'top' : 'bottom-start'}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          TransitionProps={{ timeout: 0 }}
          slotProps={{
            ...tooltipSlotProps,
            popper: {
              modifiers: [{ name: 'offset', options: { offset: [-40, -12] } }],
            },
          }}
        >
          <span
            role="button"
            tabIndex={0}
            onClick={handleToggle}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleToggle();
              }
            }}
            style={{ display: 'inline-flex', cursor: 'pointer' }}
          >
            <IconBox>
              <HintIcon />
            </IconBox>
          </span>
        </Tooltip>
      </ClickAwayListener>
    );
  }

  return (
    <Tooltip
      TransitionProps={{ timeout: 0 }}
      title={message}
      placement={placement}
      slotProps={tooltipSlotProps}
    >
      <span style={{ display: 'inline-flex' }}>
        <IconBox>
          <HintIcon />
        </IconBox>
      </span>
    </Tooltip>
  );
};
